import { Fab, useTheme } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useMemo } from "react";

import AccessControl from "@/components/AccessControl/AccessControl";
import { ExperienceService } from "@/services";
import { useThunkDispatch } from "@/store/hooks";
import {
  addExperienceMediaItemsThunk as addExperienceMediaItems,
  removeExperienceMediaItemsThunk as removeExperienceMediaItems,
} from "@/store/v2/experienceMediaItems/experienceMediaItemsThunks";
import { getThumbnailLink } from "@/utils/thumbnailLink";
import { getAllowedTiersForMediaItemSelection } from "@/utils/visualBuilder";

import { CONTENT_TYPE, EXPERIENCE_STATUS, MEDIA_SIZE, TAB_STATUSES } from "../../constants";
import { AddWhite, CheckMark, CircleChecked, CircleUnchecked } from "../../icons";
import StartingPointButton from "./Fullscreen/StartingPointButton";
import { GridItemStartingPointWrapper, StyledIconWrapper, StyledName, StyledRow } from "./styled";
import { getCurrentExperience, resetCurrentSelectedItems, updateVisualBuilderData } from "../../store/actions";
import renderTypeIcon from "../../utils/renderTypeIcon";

const useStyles = makeStyles({
  imagediv: {
    backgroundColor: "#434343",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    width: "100%",
    height: "0",
    paddingTop: "100%",
    position: "relative",
    "& img": {
      width: "100%",
      objectFit: "cover",
    },
  },
  linediv: {
    width: "100%",
    backgroundColor: "#434343",
    display: "flex",
    cursor: "pointer",
  },
  largeIcon: {
    width: "1.5rem",
    height: "1.5rem",
    stroke: "#FFF",
    fill: "none",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    "@media (max-width: 767px)": {
      width: "4rem",
      height: "4rem",
    },
    "@media (max-width: 512px)": {
      width: "3rem",
      height: "3rem",
    },
    "@media (max-width: 374px)": {
      width: "3rem",
      height: "3rem",
    },
  },
  smallicon: {
    width: "12px",
    height: "12px",
    stroke: "#FFF",
    fill: "none",
  },

  itemWidth: {
    width: "auto",
    "@media (max-width: 512px)": {
      width: "auto",
    },
    "@media (max-width: 374px)": {
      width: "auto",
    },
    "&:hover > div:last-child": {
      visibility: "visible",
    },
  },

  linerow: {
    display: "flex",
    flexDirection: "row",
    "align-items": "center",
    marginTop: "1px",
  },
  addEditAction: {
    position: "absolute",
    right: "0.1rem",
    top: "0.1rem",
    width: "22px",
    height: "22px",
    minHeight: "0px",
    overflow: "hidden",
    backgroundColor: "rgba(0,0,0,0.7)",
    color: "#FFF",
  },
  selectable: {
    position: "absolute",
    backgroundColor: "#00000080",
    width: "100%",
    height: "100%",
    top: 0,
    left: 0,
    visibility: (props) => (props.cellSelectedStatus ? "visible" : "hidden"),
  },
  circleCheck: {
    position: "absolute",
    right: 10,
    top: 10,
    width: "24px",
    height: "24px",
  },
  addIcon: {
    position: "absolute",
    right: 10,
    bottom: 10,
    transition: "right 225ms cubic-bezier(0, 0, 0.2, 1) 0ms, bottom 725ms cubic-bezier(0, 0, 0.2, 1) 0ms",
    backgroundColor: "rgba(0,0,0,0.7) !important",
    width: 28,
    height: 28,
    minHeight: 0,
    visibility: (props) => !props.collectedStatus() && "visible",
    zIndex: 1,
  },
  svgAdd: {
    fontSize: "0.7em",
  },
});

export default function MediaItemCell(props) {
  const {
    mediaItem,
    addNewExperience,
    propertyId,
    experienceId,
    selectedItems,
    experienceMediaItems,
    onElementSelect,
    params,
    disabled = false,
    startingAssetId,
    handleSetStartingPoint,
  } = props;
  const dispatch = useThunkDispatch();

  const theme = useTheme();

  const { tabStatus } = params;

  const { contentType, name } = mediaItem;

  const isOrganizationsSection = location.pathname.startsWith("/organizations");

  const url = getThumbnailLink({
    size: MEDIA_SIZE.medium,
    link:
      mediaItem.contentType.value === CONTENT_TYPE.PHOTO ||
      mediaItem.contentType.value === CONTENT_TYPE.THREE_DIMENSIONAL_MODEL
        ? mediaItem.link
        : mediaItem.thumbnailUrl,
  });

  const cellSelectedStatus = Object.hasOwn(selectedItems, mediaItem.id);

  const collectedStatus = () => {
    return !Object.hasOwn(experienceMediaItems, mediaItem.id);
  };

  const classes = useStyles({ ...props, collectedStatus, cellSelectedStatus });

  const fabToggle = async () => {
    const mediaItems = [mediaItem];
    if (tabStatus !== TAB_STATUSES.HOMEBASE) {
      if (collectedStatus()) {
        await dispatch(addExperienceMediaItems({ propertyId, experienceId, mediaItems }));
        if (!startingAssetId) {
          await ExperienceService.updateExperience(Number(propertyId), Number(experienceId), {
            startingAssetId: mediaItem.id,
          });
        }
      } else {
        await dispatch(removeExperienceMediaItems({ propertyId, experienceId, mediaItems }));
        dispatch(getCurrentExperience({ propertyId, experienceId, tabStatus }));
      }
    } else {
      await addNewExperience(mediaItems, EXPERIENCE_STATUS.DRAFT);
    }
    dispatch(resetCurrentSelectedItems());
  };

  const handleGridItemAction = () => {
    const values = { gridView: false, currentMediaItemId: mediaItem.id };

    dispatch(updateVisualBuilderData(params, values));
  };

  const allowedTiersSelection = useMemo(
    () => getAllowedTiersForMediaItemSelection(isOrganizationsSection, tabStatus === TAB_STATUSES.HOMEBASE),
    [isOrganizationsSection, tabStatus],
  );

  return (
    <div>
      <div
        data-testid="media-item-cell-container"
        className={classes.itemWidth}
        style={{
          position: "relative",
        }}
        onDoubleClick={handleGridItemAction}
      >
        <div
          data-testid="media-item-cell-img"
          className={cellSelectedStatus ? `${classes.imagediv} active` : `${classes.imagediv}`}
          style={{
            backgroundSize: "cover",
            backgroundImage: `url(${url})`,
            backgroundPosition: "center center",
          }}
        >
          {url === "" && renderTypeIcon(contentType.value, { className: classes.largeIcon, htmlColor: "#fff" })}
        </div>
        <div className={classes.selectable}>
          {renderTypeIcon(contentType.value, { className: classes.largeIcon, htmlColor: "#fff" })}
          {![TAB_STATUSES.PUBLIC_SINGLE_SHARE, TAB_STATUSES.PUBLIC].includes(tabStatus) && (
            <AccessControl allowedPermissions={["experiences:create"]} allowedTiers={allowedTiersSelection}>
              {cellSelectedStatus && (
                <CircleChecked
                  onClick={(e) => !disabled && onElementSelect(e)}
                  className={classes.circleCheck}
                  data-testid={`CircleChecked-${mediaItem.id}`}
                />
              )}
              {!cellSelectedStatus && (
                <CircleUnchecked
                  onClick={(e) => !disabled && onElementSelect(e)}
                  className={classes.circleCheck}
                  data-testid={`CircleUnchecked-${mediaItem.id}`}
                />
              )}
              <Fab
                onClick={() => fabToggle()}
                className={classes.addIcon}
                size="small"
                aria-label="add"
                disabled={disabled}
              >
                {!collectedStatus() ? (
                  <CheckMark data-testid="checkmark" htmlColor="#fff" classes={{ root: classes.svgAdd }} />
                ) : (
                  <AddWhite data-testid="add" classes={{ root: classes.svgAdd }} />
                )}
              </Fab>
            </AccessControl>
          )}
        </div>
      </div>
      <StyledRow onClick={handleGridItemAction} $theme={theme}>
        <StyledIconWrapper $theme={theme}>
          {renderTypeIcon(contentType.value, { htmlColor: theme.palette.common.white })}
        </StyledIconWrapper>
        <StyledName variant="body2" data-testid={`media-item-cell-${mediaItem.id}`} $theme={theme}>
          {name}
        </StyledName>
      </StyledRow>

      {tabStatus === TAB_STATUSES.HOMEBASE || tabStatus === TAB_STATUSES.CUSTOMIZE ? (
        <GridItemStartingPointWrapper>
          <StartingPointButton
            isStartingPoint={mediaItem.id === startingAssetId}
            onSetStartingPoint={() => handleSetStartingPoint(mediaItem.id)}
            disabled={disabled}
          />
        </GridItemStartingPointWrapper>
      ) : null}
    </div>
  );
}
